import * as Icon from "react-feather";
import { useSelector } from "react-redux";

import { useUserOptions, USEROPTION_STARTPAGE } from "@hooks/useUserOptions";

// ** Checks if an object is empty (returns boolean)
export const isObjEmpty = (obj) => Object.keys(obj).length === 0;

// ** Returns true if two objects are equal
export const areObjectsEqual = (obj1, obj2) => {
  return JSON.stringify(obj1) === JSON.stringify(obj2);
};

// ** Returns K format from a number
export const kFormatter = (num) => {
  return num > 999 ? `${(num / 1000).toFixed(1)}k` : num;
};

// ** Converts HTML to string
export const htmlToString = (html) => html.replace(/<\/?[^>]+(>|$)/g, "");

/**
 * Check whether a value is not nullish (null or undefined).
 * @param val
 * @returns {boolean} true if not null or undefined
 */
export const isNotNullish = (val) => {
  return (val ?? null) !== null;
};

/**
 * Check whether a value is nullish (null or undefined).
 * @param val
 * @returns {boolean} true if not null or undefined
 */
export const isNullish = (val) => {
  return !isNotNullish(val);
};

// ** Checks if the passed date is today
const isToday = (date) => {
  const today = new Date();
  return (
    /* eslint-disable operator-linebreak */
    date.getDate() === today.getDate() &&
    date.getMonth() === today.getMonth() &&
    date.getFullYear() === today.getFullYear()
    /* eslint-enable */
  );
};

export const isValidEmail = (email) => {
  return /^\w+([\.-]?\w+)*(\+\w+)*@\w+([\.-]?\w+)*(\.\w{2,3})+$/.test(
    String(email).toLowerCase(),
  );
};

export const isValidPhoneNumber = (phone) => {
  if (phone && phone.trim() !== "" && phone.length >= 10) {
    return true;
  }
  return false;
};

/**
 ** Format and return date in Humanize format
 ** Intl docs: https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/Intl/DateTimeFormat/format
 ** Intl Constructor: https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/Intl/DateTimeFormat/DateTimeFormat
 * @param {String} value date to format
 * @param {Object} formatting Intl object to format with
 */
export const formatDate = (
  value,
  formatting = { month: "short", day: "numeric", year: "numeric" },
) => {
  if (!value) return value;
  return new Intl.DateTimeFormat("en-US", formatting).format(new Date(value));
};

// ** Returns short month of passed date
export const formatDateToMonthShort = (value, toTimeForCurrentDay = true) => {
  const date = new Date(value);
  let formatting = { month: "short", day: "numeric" };

  if (toTimeForCurrentDay && isToday(date)) {
    formatting = { hour: "numeric", minute: "numeric" };
  }

  return new Intl.DateTimeFormat("en-US", formatting).format(new Date(value));
};

export const monthNames = () => [
  "Jan",
  "Feb",
  "Mar",
  "Apr",
  "May",
  "Jun",
  "Jul",
  "Aug",
  "Sept",
  "Oct",
  "Nov",
  "Dec",
];

/**
 ** Returns the truncated month name given an index from 0 to 11
 * @param index
 * @returns {string}
 */
export const monthShortNameFromIndex = (index) => {
  const names = monthNames();
  return names[index];
};

/**
 ** Return if user is logged in
 ** This is completely up to you and how you want to store the token in your frontend application
 *  ? e.g. If you are using cookies to store the application please update this function
 */
export const isUserLoggedIn = () => {
  return localStorage.getItem("authToken") && localStorage.getItem("userData");
};
export const getUserData = () => {
  const pstore = useSelector((state) => state.persistedAppData);
  return pstore.userinfo || null;
  // JSON.parse(localStorage.getItem('userData'))
};

/**
 ** This function is used for demo purpose route navigation
 ** In real app you won't need this function because your app will navigate to same route for each users regardless of ability
 ** Please note role field is just for showing purpose it's not used by anything in frontend
 ** We are checking role just for ease
 * ? NOTE: If you have different pages to navigate based on user ability then this function can be useful. However, you need to update it.
 * @param {String} userRole Role of user
 */
export const getHomeRouteForLoggedInUser = (userRole) => {
  const useroptions = useUserOptions();
  return useroptions.get(USEROPTION_STARTPAGE);
  const pstore = useSelector((state) => state.persistedAppData);
  if (pstore.userinfo && pstore.userinfo.options) {
    return pstore.userinfo.options["USEROPTION_STARTPAGE"] || "/buildingslist";
  }
  if (userRole) {
  }
  // if (userRole === 'admin') return DefaultRoute
  // if (userRole === 'client') return '/access-control'
  return "/";
};

// ** React Select Theme Colors
export const selectThemeColors = (theme) => ({
  ...theme,
  colors: {
    ...theme.colors,
    primary25: "#7367f01a", // for option hover bg-color
    primary: "#7367f0", // for selected option bg-color
    neutral10: "#7367f0", // for tags bg-color
    neutral20: "#ededed", // for input border-color
    neutral30: "#ededed", // for input hover border-color
  },
});

export const patchIcons = (suggestions) => {
  return suggestions.map((group) => {
    return {
      ...group,
      data: group.data.map((e) => {
        let newIcon = Icon[e.icon ? e.icon : "X"];
        if (newIcon === undefined) {
          newIcon = <X size={20} />;
        }
        // const newIcon = <X size={20} />    // default icon
        // switch (e.icon) {
        //   case 'home':
        //     newIcon = <ExternalLink size={20}/>
        //     break
        //   case 'mail':
        //     newIcon = <Mail size={100}/>
        //     break
        //   case 'externallink':
        //     newIcon = <ExternalLink size={20} />
        //     break
        //   default:
        //     break
        // } // icons switch
        return { ...e, icon: newIcon };
      }), // datamap
    }; // each group
  }); // groups
}; // patch
